import { useState, useMemo } from "react";
import authFetch from "../authFetch";
import { useValidationToast, validateFields } from "../utils/validation";
import { TASK_TAXONOMY_SEED } from "../state/seed";

/* ── Super Admin: Task Taxonomy Panel ──────────────────────
   Edits platform.taskTaxonomy — the SA layer in the three-layer
   merge (seed → platform → workspace). See
   refactor-plan-task-taxonomy.md §2.2 and §3. Facility renders
   as parent → children tree; other modules render flat. */
const MODULES = ["Facility", "Fleet", "Household", "Projects", "Transport", "Menu"];
const PRIORITIES = ["low", "medium", "high", "urgent"];

/* Merge seed + platform into a single editable view. Each entry
   carries its `source` tag so the UI can show a "Default" badge
   and disable destructive actions on seed rows. */
function mergeForModule(platformEntries, module) {
  const byId = new Map();
  for (const e of TASK_TAXONOMY_SEED) if (e.module === module) byId.set(e.id, { ...e, source: "seed" });
  for (const e of platformEntries || []) if (e.module === module) byId.set(e.id, { ...(byId.get(e.id) || {}), ...e, source: "platform" });
  return Array.from(byId.values());
}

export default function SATaskTaxonomyPanel({ platform, dispatch }) {
  const vt = useValidationToast();
  const [module, setModule] = useState("Facility");
  const [saving, setSaving] = useState(false);
  const [showAdd, setShowAdd] = useState(null); /* {module, parent} */
  const [addForm, setAddForm] = useState(null);

  const platformEntries = platform.taskTaxonomy || [];
  /* SA panel uses the platform-level expense categories (the source of
     truth copied to new workspaces at signup). Workspace overrides are
     applied in Phase 4's workspace-side editor, not here. */
  const allExpenseCats = platform?.platformCategories?.expense || [];
  const expenseCats = allExpenseCats.filter(c => !c.parent);
  const subCatsOf = (catId) => allExpenseCats.filter(c => c.parent === catId);

  const visible = useMemo(() => mergeForModule(platformEntries, module), [platformEntries, module]);
  const roots = visible.filter(e => !e.parent);
  const childrenOf = (pid) => visible.filter(e => e.parent === pid);

  /* Inline patch writes to platform.taskTaxonomy; auto-persists
     via usePersistedReducer. The explicit POST below mirrors the
     save_config wiring so the SA panel never silently drops edits. */
  const patchEntry = (entry, patch) => {
    const existsInPlatform = platformEntries.some(e => e.id === entry.id);
    if (existsInPlatform) {
      dispatch({ type: "UPDATE_TASK_TAXONOMY_ENTRY", id: entry.id, patch });
    } else {
      /* Seed override: copy the seed row into platform with the patch applied */
      dispatch({ type: "ADD_TASK_TAXONOMY_ENTRY", entry: { ...entry, ...patch, updatedAt: new Date().toISOString() } });
    }
  };

  const addEntry = () => {
    const f = addForm;
    const m = validateFields([{ ok: f.value.trim(), label: "Value" }, { ok: f.label.trim(), label: "Label" }]);
    if (m.length) { vt.show(m.join(", ")); return; }
    const entry = {
      id: `tax_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      module: showAdd.module,
      parent: showAdd.parent || null,
      value: f.value.trim().toLowerCase().replace(/\s+/g, "_"),
      label: f.label.trim(),
      labelAr: f.labelAr.trim(),
      defaultPriority: f.defaultPriority || "medium",
      requiresApproval: !!f.requiresApproval,
      requiresExpense: !!f.requiresExpense,
      enabled: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    dispatch({ type: "ADD_TASK_TAXONOMY_ENTRY", entry });
    setShowAdd(null); setAddForm(null);
  };

  const removeEntry = (entry) => {
    if (entry.source === "seed") { vt.show("Cannot delete a seed default — disable it instead."); return; }
    if (!window.confirm(`Delete "${entry.label}"?`)) return;
    dispatch({ type: "REMOVE_TASK_TAXONOMY_ENTRY", id: entry.id });
  };

  /* Explicit server-side persistence: writes the full platform
     taskTaxonomy array into cmd_platform.taskTaxonomy. Runs on
     demand from the footer Save button. */
  const saveToServer = async () => {
    setSaving(true);
    try {
      const r = await authFetch("/api/tasks", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "save_platform_taxonomy", entries: platformEntries }),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); vt.show(`Save failed: ${d.error || r.status}`); }
      else vt.show("Taxonomy saved.");
    } catch (e) { vt.show(`Save failed: ${e.message}`); }
    finally { setSaving(false); }
  };

  const badge = (src) => {
    const colors = { seed: "var(--cg400)", platform: "var(--caccent,#C9A84C)", workspace: "#22c55e" };
    return <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: colors[src] || "var(--cg400)", padding: "2px 6px", border: `1px solid ${colors[src] || "var(--cg300)"}`, borderRadius: 4, marginInlineStart: 6 }}>{src === "seed" ? "Default" : src}</span>;
  };

  const row = (e, indent = 0) => (
    <tr key={e.id} style={{ borderBottom: "1px solid var(--cg100)" }}>
      <td style={{ padding: "8px 10px", paddingInlineStart: 10 + indent * 20, color: "var(--cink)" }}>
        <span style={{ fontWeight: indent ? 400 : 600 }}>{e.label}</span>{badge(e.source)}
        <div style={{ fontSize: 11, color: "var(--cg400)", fontFamily: "var(--fm,monospace)" }}>{e.value}</div>
      </td>
      <td style={{ padding: "4px 6px" }}>
        <input className="p-input" style={{ width: "100%", fontSize: 12 }} defaultValue={e.label} onBlur={ev => ev.target.value !== e.label && patchEntry(e, { label: ev.target.value })} />
      </td>
      <td style={{ padding: "4px 6px" }}>
        <input className="p-input" style={{ width: "100%", fontSize: 12 }} dir="rtl" defaultValue={e.labelAr || ""} onBlur={ev => ev.target.value !== (e.labelAr || "") && patchEntry(e, { labelAr: ev.target.value })} />
      </td>
      <td style={{ padding: "4px 6px" }}>
        <select className="p-input" style={{ fontSize: 12 }} value={e.defaultPriority || "medium"} onChange={ev => patchEntry(e, { defaultPriority: ev.target.value })}>
          {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
      </td>
      <td style={{ padding: "4px 6px", textAlign: "center" }}>
        <input type="checkbox" checked={!!e.requiresApproval} onChange={ev => patchEntry(e, { requiresApproval: ev.target.checked })} />
      </td>
      <td style={{ padding: "4px 6px", textAlign: "center" }}>
        <input type="checkbox" checked={!!e.requiresExpense} onChange={ev => patchEntry(e, { requiresExpense: ev.target.checked })} />
      </td>
      <td style={{ padding: "4px 6px", minWidth: 180 }}>
        <select className="p-input" style={{ fontSize: 12, width: "100%" }} value={e.defaultExpenseCatId || ""} onChange={ev => {
          const catId = ev.target.value || null;
          /* Clear sub when the parent cat changes so an orphan sub
             doesn't stick around. Mirrors workspace TaxonomyEditor. */
          const patch = { defaultExpenseCatId: catId };
          if (!catId || !subCatsOf(catId).some(s => s.id === e.defaultExpenseSubId)) patch.defaultExpenseSubId = null;
          patchEntry(e, patch);
        }}>
          <option value="">— none —</option>
          {expenseCats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {e.defaultExpenseCatId && subCatsOf(e.defaultExpenseCatId).length > 0 && (
          <select className="p-input" style={{ fontSize: 12, width: "100%", marginTop: 4 }} value={e.defaultExpenseSubId || ""} onChange={ev => patchEntry(e, { defaultExpenseSubId: ev.target.value || null })}>
            <option value="">— any —</option>
            {subCatsOf(e.defaultExpenseCatId).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}
      </td>
      <td style={{ padding: "4px 6px", textAlign: "center" }}>
        <input type="checkbox" checked={e.enabled !== false} onChange={ev => patchEntry(e, { enabled: ev.target.checked })} />
      </td>
      <td style={{ padding: "4px 6px", whiteSpace: "nowrap" }}>
        {module === "Facility" && !e.parent && (
          <button className="p-btn-s" style={{ fontSize: 10, padding: "3px 6px", marginInlineEnd: 4 }} onClick={() => { setShowAdd({ module, parent: e.id }); setAddForm({ value: "", label: "", labelAr: "", defaultPriority: "medium", requiresApproval: false, requiresExpense: false }); }}>+ Type</button>
        )}
        {e.source !== "seed" && (
          <button className="p-btn-s" style={{ fontSize: 10, padding: "3px 6px", borderColor: "#ef4444", color: "#ef4444" }} onClick={() => removeEntry(e)}>Del</button>
        )}
      </td>
    </tr>
  );

  return (
    <div style={{ maxWidth: 1100 }}>
      {vt.Toast}
      <div className="p-section-hd" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span>Task Taxonomy</span>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="p-btn-s" style={{ fontSize: 12, padding: "6px 14px" }} onClick={() => { setShowAdd({ module, parent: null }); setAddForm({ value: "", label: "", labelAr: "", defaultPriority: "medium", requiresApproval: false, requiresExpense: false }); }}>+ Add {module === "Facility" ? "Category" : "Entry"}</button>
          <button className="p-btn-p" style={{ fontSize: 12, padding: "6px 14px" }} onClick={saveToServer} disabled={saving}>{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>

      <div style={{ display: "flex", gap: 4, marginBottom: 12, flexWrap: "wrap" }}>
        {MODULES.map(mk => (
          <button key={mk} className={module === mk ? "p-btn-p" : "p-btn-s"} style={{ fontSize: 12, padding: "6px 12px" }} onClick={() => setModule(mk)}>{mk}</button>
        ))}
      </div>

      <div className="p-card">
        <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
          <thead><tr style={{ borderBottom: "1px solid var(--cg200)", textAlign: "left" }}>
            {["Name / Value", "Label", "Label AR", "Priority", "Appr.", "Exp.", "Expense Cat", "On", ""].map((h, i) => (
              <th key={i} style={{ padding: "8px 10px", color: "var(--cg400)", fontWeight: 600, fontSize: 11, textTransform: "uppercase" }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {module === "Facility"
              ? roots.flatMap(r => [row(r, 0), ...childrenOf(r.id).map(c => row(c, 1))])
              : visible.map(e => row(e, 0))}
          </tbody>
        </table>
        {!visible.length && <div style={{ padding: 24, textAlign: "center", color: "var(--cg400)", fontSize: 13 }}>No entries for this module yet.</div>}
      </div>

      {showAdd && addForm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => { setShowAdd(null); setAddForm(null); }}>
          <div style={{ background: "var(--cw,#fff)", borderRadius: 16, width: "100%", maxWidth: 520, boxShadow: "0 24px 48px rgba(0,0,0,.3)" }} onClick={ev => ev.stopPropagation()}>
            <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--cg100)", fontSize: 16, fontWeight: 600, color: "var(--cink)" }}>New {showAdd.parent ? "Type" : "Entry"} — {showAdd.module}{showAdd.parent && <span style={{ color: "var(--cg400)", fontWeight: 400, fontSize: 13 }}> under {visible.find(v => v.id === showAdd.parent)?.label}</span>}</div>
            <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ display: "flex", gap: 12 }}>
                <div style={{ flex: 1 }}><div style={{ fontSize: 11, fontWeight: 600, color: "var(--cg400)", marginBottom: 4, textTransform: "uppercase" }}>Value (key)</div><input className="p-input" style={{ width: "100%" }} value={addForm.value} onChange={ev => setAddForm(p => ({ ...p, value: ev.target.value }))} placeholder="e.g. deep_clean" /></div>
                <div style={{ flex: 1 }}><div style={{ fontSize: 11, fontWeight: 600, color: "var(--cg400)", marginBottom: 4, textTransform: "uppercase" }}>Priority</div><select className="p-input" style={{ width: "100%" }} value={addForm.defaultPriority} onChange={ev => setAddForm(p => ({ ...p, defaultPriority: ev.target.value }))}>{PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}</select></div>
              </div>
              <div style={{ display: "flex", gap: 12 }}>
                <div style={{ flex: 1 }}><div style={{ fontSize: 11, fontWeight: 600, color: "var(--cg400)", marginBottom: 4, textTransform: "uppercase" }}>Label</div><input className="p-input" style={{ width: "100%" }} value={addForm.label} onChange={ev => setAddForm(p => ({ ...p, label: ev.target.value }))} /></div>
                <div style={{ flex: 1 }}><div style={{ fontSize: 11, fontWeight: 600, color: "var(--cg400)", marginBottom: 4, textTransform: "uppercase" }}>Label (AR)</div><input className="p-input" style={{ width: "100%" }} dir="rtl" value={addForm.labelAr} onChange={ev => setAddForm(p => ({ ...p, labelAr: ev.target.value }))} /></div>
              </div>
              <div style={{ display: "flex", gap: 20, fontSize: 13, color: "var(--cink)" }}>
                <label style={{ display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}><input type="checkbox" checked={addForm.requiresApproval} onChange={ev => setAddForm(p => ({ ...p, requiresApproval: ev.target.checked }))} /> Requires Approval</label>
                <label style={{ display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}><input type="checkbox" checked={addForm.requiresExpense} onChange={ev => setAddForm(p => ({ ...p, requiresExpense: ev.target.checked }))} /> Requires Expense</label>
              </div>
            </div>
            <div style={{ padding: "14px 20px", borderTop: "1px solid var(--cg100)", display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button className="p-btn-s" onClick={() => { setShowAdd(null); setAddForm(null); }}>Cancel</button>
              <button className="p-btn-p" onClick={addEntry}>Add</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
